import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { DragEndEvent, DragOverEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useCallback, useState } from "react";
import { startOfDay } from "date-fns/startOfDay";
import { DRAG_ACTIVATION_DISTANCE_PX } from "../constants";
import { suppressNextClick } from "../suppress-next-click";
import { useUnavailableUsersGuard } from "../../hooks";

type CalendarEvent = TellescopeModelFromName<"calendar_events">;

const getAttendeeUserIds = (event: CalendarEvent) =>
  event.attendees.reduce<string[]>((acc, attendee) => {
    if (attendee.type === "user") acc.push(attendee.id);
    return acc;
  }, []);

export type MonthViewDragData = { type: "move-day"; event: CalendarEvent; sourceDayStart: number };
export type MonthViewDroppableData = { dayDate: Date };

export interface ActiveMonthViewDrag {
  event: CalendarEvent;
  sourceDayStart: number;
  hoveredDayStart: number | null;
}

export const useMonthViewDragLogic = ({ timezone }: { timezone?: string } = {}) => {
  const [activeDrag, setActiveDrag] = useState<ActiveMonthViewDrag | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: DRAG_ACTIVATION_DISTANCE_PX },
    })
  );

  const updateEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "updateOne",
    optimisticUpdates: true,
    autoCacheUpdateStrategy: "update",
  });

  const { guard: guardUpdate, warningDialogProps: updateWarningDialogProps } = useUnavailableUsersGuard({ timezone });

  const handleDragStart = useCallback((e: DragStartEvent) => {
    const data = e.active.data.current;
    if (!data) return;
    setActiveDrag({ event: data.event, sourceDayStart: data.sourceDayStart, hoveredDayStart: null });
  }, []);

  const handleDragOver = useCallback((e: DragOverEvent) => {
    const overData = e.over?.data.current;
    const hoveredDayStart = overData ? startOfDay(overData.dayDate).getTime() : null;
    setActiveDrag((prev) => (prev ? { ...prev, hoveredDayStart } : prev));
  }, []);

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const data = e.active.data.current;
      const overData = e.over?.data.current;
      setActiveDrag(null);
      suppressNextClick();
      if (!data || !overData) return;

      const targetDayStart = startOfDay(overData.dayDate).getTime();
      const eventStartDay = startOfDay(new Date(data.event.startTimeInMS)).getTime();
      const dayDeltaMs = targetDayStart - eventStartDay;
      if (dayDeltaMs === 0) return;

      const newStart = data.event.startTimeInMS + dayDeltaMs;

      guardUpdate(
        {
          userIds: getAttendeeUserIds(data.event),
          start: new Date(newStart),
          durationInMinutes: data.event.actualDuration ?? data.event.durationInMinutes,
        },
        () =>
          updateEventMutation.mutate({
            id: data.event.id,
            startTimeInMS: newStart,
          })
      );
    },
    [updateEventMutation, guardUpdate]
  );

  const handleDragCancel = useCallback(() => {
    setActiveDrag(null);
    suppressNextClick();
  }, []);

  return {
    sensors,
    activeDrag,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    handleDragCancel,
    updateWarningDialogProps,
  };
};
